import { useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { scrollToSection } from "@/lib/scrollToSection";
import { motion } from "framer-motion";
import { ArrowRight, Check, Zap, Shield, Globe, PlayCircle } from "lucide-react";
import { T } from "@/components/T";
import { Button } from "@/components/ui/button";
import { TOOL_REGISTRY } from "@/lib/toolRegistry";
import geniebotLogo from "@/assets/geniebot-robot-logo.webp";
import introAsset from "@/assets/geniebot-intro.mp4.asset.json";
const introVideo = introAsset.url;


import { EcosystemBar } from "@/components/landing/EcosystemBar";
import { HeroToolsShowcase } from "@/components/landing/HeroToolsShowcase";
import { CommercialShowcase } from "@/components/landing/CommercialShowcase";
import { FindYourGenieBot } from "@/components/landing/FindYourGenieBot";
import { CategoryShowcase } from "@/components/landing/CategoryShowcase";
import { WhyGenieBot } from "@/components/landing/WhyGenieBot";
import { UseCasePersonas } from "@/components/landing/UseCasePersonas";
import { LandingGenieBotFamily } from "@/components/landing/LandingGenieBotFamily";
import { LandingFAQ } from "@/components/landing/LandingFAQ";
import { StickyMobileCTA } from "@/components/landing/StickyMobileCTA";
import { HireAIKenny } from "@/components/booking/HireAIKenny";
import { CourseSpotlight } from "@/components/landing/CourseSpotlight";
import { LearnCallout } from "@/components/learn/LearnCallout";
import { GoalPicker } from "@/components/startHere/GoalPicker";
import { PRODUCT } from "@/config/product";
import { TOOL_GROUPS } from "@/config/toolTaxonomy";
import { ALL_LESSONS } from "@/config/learningHub";

import { SectionBoundary } from "@/components/SectionBoundary";

const stats = [
  { label: "AI Tools", value: `${TOOL_REGISTRY.length}` },
  { label: "Free lessons", value: `${ALL_LESSONS.length}` },
  { label: "Tool groups", value: `${TOOL_GROUPS.length}` },
  { label: "To get started", value: "$0" },
];

const freePlanFeatures = [
  "AI Chat with multiple models",
  "Everyday tools",
  "Prompt Studio & Analyzer",
  "Image generation",
  "Games Arcade",
  "Community prompts",
];

const proPlanFeatures = [
  "Everything in Free",
  "All premium tools unlocked",
  "GenieBot Scraper (unlimited)",
  "AI Video Generator",
  "Priority support",
  "Early access to new features",
];

import { SEO } from "@/components/SEO";

const Landing = () => {
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Handles direct loads (/#hire-ai-kenny), in-app navigation with a hash, and
  // repeat clicks on the same hash link. Retries while lazy sections mount.
  useEffect(() => {
    const hash = (location.hash || window.location.hash).replace('#', '');
    if (!hash) return;
    let cancelled = false;
    void scrollToSection(hash).then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [location.hash, location.key]);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden" role="main">
      <SEO
        title="GenieBot — Learn AI, Create with AI, Get Things Done"
        description={PRODUCT.description}
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "GenieBot",
            url: "https://geniebot.pro",
            logo: "https://geniebot.pro/icon-192.png",
            description: PRODUCT.description,
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "GenieBot Pro",
            url: "https://geniebot.pro",
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: "https://geniebot.pro/tool-finder?q={search_term_string}",
              },
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />
      {/* Ecosystem Bar */}
      <section aria-labelledby="ecosystem-heading">
        <h2 id="ecosystem-heading" className="sr-only"><T>The GenieBot ecosystem</T></h2>
        <SectionBoundary name="EcosystemBar"><EcosystemBar /></SectionBoundary>
      </section>

      {/* Hero */}
      <section className="relative py-16 sm:py-24 md:py-36 px-4">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.12),transparent)]" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="flex items-center justify-center mb-6 sm:mb-8">
              <div className="relative">
                <img
                  src={geniebotLogo}
                  alt="GenieBot AI robot mascot logo"
                  className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl object-cover border-2 border-primary shadow-lg"
                  width={64}
                  height={64}
                />
              </div>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight mb-4 sm:mb-6 leading-[1.1]">
              <T>Learn AI. Create with AI.</T>{" "}
              <span className="text-primary"><T>Get more done.</T></span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed px-2">
              <T>Tell GenieBot what you want to accomplish. Free AI chat, practical tools, and short lessons from AI Kenny take you the rest of the way.</T>
            </p>
            <div ref={heroCtaRef} className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 flex-wrap">
              <Button asChild size="lg" className="text-base px-8 gap-2 h-12 w-full sm:w-auto">
                <Link to="/auth">
                  <T>Get Started Free</T> <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="text-base px-8 h-12 w-full sm:w-auto gap-2" onClick={() => void scrollToSection('choose-goal')}>
                <T>See what you can do</T>
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              <T>Free to start · No credit card needed for the free plan</T> ·{" "}
              <Link to="/legal/privacy" className="underline underline-offset-2 hover:text-foreground"><T>How we handle your data</T></Link>
            </p>
          </motion.div>
        </div>
      </section>

      {/* What do you want to do? */}
      <SectionBoundary name="GoalPicker"><GoalPicker /></SectionBoundary>

      {/* Product Tour Video */}
      <section id="product-tour" className="py-16 sm:py-20 px-4 bg-muted/30 border-y border-border/40" aria-labelledby="tour-heading">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
              <PlayCircle className="h-3.5 w-3.5" /> <T>Product Tour</T>
            </div>
            <h2 id="tour-heading" className="text-3xl md:text-4xl font-bold mb-3">
              <T>See GenieBot in Action</T>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              <T>A narrated walkthrough of every major tool — Chat, Skill Builder, Chatbot Builder, Contract Analyzer, Workflow Builder, and more.</T>
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden border border-border shadow-2xl shadow-primary/10 bg-card"
          >
            <video
              src={introVideo}
              controls
              preload="metadata"
              playsInline
              className="w-full aspect-video bg-black"
              aria-label="GenieBot product tour video"
            />
          </motion.div>
        </div>
      </section>


      {/* Stats Strip */}
      <section className="py-8 sm:py-10 border-y border-border/40 bg-muted/30" aria-labelledby="stats-heading">
        <h2 id="stats-heading" className="sr-only"><T>GenieBot platform statistics</T></h2>
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary">{stat.value}</div>
              <div className="text-xs sm:text-sm text-muted-foreground mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 30-Second Commercial */}
      <SectionBoundary name="CommercialShowcase"><CommercialShowcase /></SectionBoundary>

      {/* Start Here — hero tools */}
      <SectionBoundary name="HeroToolsShowcase"><HeroToolsShowcase /></SectionBoundary>

      {/* Find Your GenieBot Quiz */}
      <SectionBoundary name="FindYourGenieBot"><FindYourGenieBot /></SectionBoundary>

      {/* Tool Categories */}
      <SectionBoundary name="CategoryShowcase"><CategoryShowcase /></SectionBoundary>

      {/* Why GenieBot */}
      <SectionBoundary name="WhyGenieBot"><WhyGenieBot /></SectionBoundary>

      {/* Hire AI Kenny */}
      <SectionBoundary name="HireAIKenny"><HireAIKenny /></SectionBoundary>

      {/* Course spotlight */}
      <SectionBoundary name="CourseSpotlight"><CourseSpotlight /></SectionBoundary>
      <SectionBoundary name="LearnCallout"><div className="mx-auto max-w-5xl px-4 py-8"><LearnCallout /></div></SectionBoundary>



      {/* Use-Case Personas */}
      <SectionBoundary name="UseCasePersonas"><UseCasePersonas /></SectionBoundary>

      {/* GenieBot Family */}
      <SectionBoundary name="LandingGenieBotFamily"><LandingGenieBotFamily /></SectionBoundary>

      {/* Pricing */}
      <section className="py-16 sm:py-20 px-4" id="pricing" aria-labelledby="pricing-heading">
        <div className="max-w-4xl mx-auto">
          <h2 id="pricing-heading" className="text-3xl md:text-4xl font-bold text-center mb-3"><T>Simple, Transparent Pricing</T></h2>
          <p className="text-muted-foreground text-center mb-10 sm:mb-12"><T>Start free. Upgrade when you need more.</T></p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {/* Free */}
            <div className="p-6 sm:p-8 rounded-2xl border border-border bg-card">
              <h3 className="text-xl font-bold mb-2"><T>Free</T></h3>
              <div className="text-3xl sm:text-4xl font-bold mb-6">$0<span className="text-base font-normal text-muted-foreground">/<T>mo</T></span></div>
              <ul className="space-y-3 mb-8">
                {freePlanFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
                    <span><T>{f}</T></span>
                  </li>
                ))}
              </ul>
              <Button asChild variant="outline" className="w-full h-11">
                <Link to="/auth"><T>Get Started</T></Link>
              </Button>
            </div>
            {/* Pro */}
            <div className="p-6 sm:p-8 rounded-2xl border-2 border-primary bg-card relative shadow-[0_0_40px_hsl(var(--primary)/0.08)]">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                <T>MOST POPULAR</T>
              </div>
              <h3 className="text-xl font-bold mb-2">GenieBot Pro</h3>
              <div className="text-3xl sm:text-4xl font-bold mb-6">$8.99<span className="text-base font-normal text-muted-foreground">/<T>mo</T></span></div>
              <ul className="space-y-3 mb-8">
                {proPlanFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
                    <span><T>{f}</T></span>
                  </li>
                ))}
              </ul>
              <Button asChild className="w-full h-11">
                <Link to="/auth"><T>Start 14-Day Free Trial</T></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 px-4 bg-muted/20" aria-labelledby="trust-heading">
        <h2 id="trust-heading" className="text-3xl md:text-4xl font-bold text-center mb-10"><T>Built on Security, Speed, and Global Reach</T></h2>
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8 text-center">
          <div>
            <Shield className="h-8 w-8 text-primary mx-auto mb-3" />
            <h3 className="font-semibold mb-1"><T>AES-256 Encryption</T></h3>
            <p className="text-sm text-muted-foreground"><T>Your data is encrypted at rest and in transit.</T></p>
          </div>
          <div>
            <Zap className="h-8 w-8 text-primary mx-auto mb-3" />
            <h3 className="font-semibold mb-1"><T>Blazing Fast</T></h3>
            <p className="text-sm text-muted-foreground"><T>Optimized infrastructure for speed and reliability.</T></p>
          </div>
          <div>
            <Globe className="h-8 w-8 text-primary mx-auto mb-3" />
            <h3 className="font-semibold mb-1"><T>Multi-Language</T></h3>
            <p className="text-sm text-muted-foreground"><T>Support for 20+ languages across all tools.</T></p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <SectionBoundary name="LandingFAQ"><LandingFAQ /></SectionBoundary>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 px-4 bg-muted/30 border-t border-border/40">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4"><T>Ready to Work Smarter?</T></h2>
          <p className="text-muted-foreground mb-8 text-lg"><T>Pick a goal, start free, and let GenieBot point you to the right tool or lesson.</T></p>
          <Button asChild size="lg" className="text-base px-8 gap-2 h-12">
            <Link to="/auth">
              <T>Create Your Free Account</T> <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>


      {/* Sticky Mobile CTA */}
      <SectionBoundary name="StickyMobileCTA"><StickyMobileCTA heroRef={heroCtaRef} /></SectionBoundary>
    </div>
  );
};

export default Landing;
